<template lang="pug">
.loading(v-if="twinLoadingState.loading")
  el-skeleton(:rows="4", animated)
el-alert(
  v-else-if="!twinLoadingState.loading && twinLoadingState.error",
  type="error",
  :closable="false"
) {{ $t("genericError") }}
TicketView(
  v-if="ticket",
  :ticket="ticket",
  :useAuth="useAssetTicketManagementAuth",
  :ticket-template-query="{ type: assetManagementTicketType }",
  :preview="props.preview",
  ticketHistoryViewName="assetManagementTicketHistory",
  hide-sops
)
  template(#additionalHeaderFields)
    .tm-item
      .smx-label {{ $t("asset") }}
      el-popover(trigger="click", :visible="assetPopoverVisible", width="300")
        template(#reference)
          .flex.flex-row.items-center(@click="showAssetPopover")
            TwinField(:twin="propValuesByCustomId.asset?.[0]")
            el-icon.cursor-pointer.ml-4(
              v-if="isAssetEditable",
              @click="showAssetPopover"
            )
              Edit
        .flex-col
          TwinSelect(
            v-model="asset",
            :multiple="false",
            :query="{ isTemplate: false, type: productionPrefix + '*', active: true }",
            :props-in-label="['location', 'serialNumber']",
            show-details
          )
          .flex.justify-end.mt-4
            el-button(type="danger", @click="assetPopoverVisible = false") {{ $t("cancel") }}
            el-button(
              v-loading="assetLoading",
              type="primary",
              @click="saveAsset"
            ) {{ $t("save") }}
</template>

<script setup lang="ts">
import { type TwinManagementTypes } from "@inctec/smx-ui-client";
import TicketView from "@shared/components/tickets/TicketView.vue";
import { computed, onBeforeMount, ref, watch } from "vue";
import { useTwinStore } from "@shared/stores/twinManagement";
import { useAssetTicketManagementAuth } from "../hooks/assetTicketManagementAuth";
import { type TwinResponse } from "@shared/services/twinManagement";
import { type LoadState } from "@shared/models/state";
import {
  assetManagementTicketProps,
  assetManagementTicketType,
  prefix as productionPrefix,
} from "../constants";
import { statuses } from "@shared/components/tickets/constants";
import TwinField from "@shared/components/twinManagement/components/TwinField.vue";
import TwinSelect from "@shared/components/twinManagement/components/TwinSelect.vue";
import { showResponseErrorToUser } from "@shared/util/genericResponseErrors";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  id?: string;
  ticket?: TwinResponse;
  preview?: boolean;
}>();

const emits = defineEmits<{
  ticketLoadState: [state: LoadState<TwinResponse>];
}>();

const twinStore = useTwinStore();
const { t } = useI18n();

watch(
  () => props.id,
  () => {
    if (props.id) {
      void twinStore.loadTwin(props.id);
    }
  },
);

const twinLoadingState = computed(() => twinStore.twinLoadingState);

watch(
  twinLoadingState,
  (newState) => {
    emits("ticketLoadState", newState);
  },
  { immediate: true },
);

const ticket = computed(() => props.ticket || twinLoadingState.value.result);

onBeforeMount(() => {
  if (props.id) {
    void twinStore.loadTwin(props.id);
  }
});

const propValuesByCustomId = computed(() => {
  const o: Record<string, any> = {};
  ticket.value?.properties?.forEach((prop) => {
    o[prop.customId!] = prop.propertyValue?.value;
  });
  return o;
});

const auth = useAssetTicketManagementAuth();

const status = computed(() => {
  return ticket.value?.properties?.find((item) => item.customId === "status")
    ?.propertyValue?.value as string;
});

const isBeforeCompleted = computed(() => {
  return [
    statuses.draft,
    statuses.planned,
    statuses.new,
    statuses.inProcess,
  ].includes(status.value);
});

// asset
const asset = ref<TwinManagementTypes.TwinResponse[] | null>(null);
const assetLoading = ref(false);
const assetPopoverVisible = ref(false);

const isAssetEditable = computed(() => {
  return (
    (auth.isAdmin() || auth.isTicketTypeManager(ticket.value!)) &&
    isBeforeCompleted.value
  );
});

function showAssetPopover() {
  asset.value = propValuesByCustomId.value?.["asset"] as
    | TwinManagementTypes.TwinResponse[]
    | null;
  assetPopoverVisible.value = true;
}

async function saveAsset() {
  if (asset.value && ticket.value) {
    assetLoading.value = true;
    const propertyUuid = ticket.value.properties!.find(
      (item) => item.customId === "asset",
    )?.uuid;
    if (propertyUuid === undefined) {
      throw new Error("UUID for asset property not found");
    }
    await twinStore
      .editTwin(ticket.value, {
        properties: [
          {
            propertyUuid,
            value: asset.value?.[0]?.uuid ?? "",
          },
        ],
      })
      .then(
        () => {},
        (e) => {
          showResponseErrorToUser(e, t);
        },
      )
      .finally(() => {
        assetLoading.value = false;
      });
  }
  assetPopoverVisible.value = false;
}

// Responsibility
const responsibility = ref<string | null>(null);
const responsibilityLoading = ref(false);
const responsibilityPopoverVisible = ref(false);

const isResponsibilityEditable = computed(
  () => ![statuses.completed, statuses.confirmed].includes(status.value),
);

function showResponsibilityPopover() {
  responsibility.value = propValuesByCustomId.value["responsibility"] as string;
  responsibilityPopoverVisible.value = true;
}

async function saveResponsibility() {
  if (responsibility.value && ticket.value) {
    responsibilityLoading.value = true;
    const propertyUuid = ticket.value.properties!.find(
      (item) => item.customId === assetManagementTicketProps.responsibility,
    )?.uuid;
    if (propertyUuid === undefined) {
      throw new Error("UUID for responsibility property not found");
    }
    await twinStore
      .editTwin(ticket.value, {
        properties: [
          {
            propertyUuid,
            value: responsibility.value,
          },
        ],
      })
      .then(
        () => {},
        (e) => {
          showResponseErrorToUser(e, t);
        },
      )
      .finally(() => {
        responsibilityLoading.value = false;
      });
  }
  responsibilityPopoverVisible.value = false;
}

// Area
const isLocationEditable = computed(() => {
  return (
    (auth.isAdmin() || auth.isTicketTypeManager(ticket.value!)) &&
    isBeforeCompleted.value
  );
});

async function saveLocation(value: string) {
  if (!ticket.value) return;
  const propertyUuid = ticket.value.properties!.find(
    (item) => item.customId === assetManagementTicketProps.area,
  )?.uuid;
  if (propertyUuid === undefined) {
    throw new Error("UUID for area property not found");
  }
  await twinStore.editTwin(ticket.value, {
    properties: [
      {
        propertyUuid,
        value,
      },
    ],
  });
}
</script>
